// FILE: src/lib/validations/claim.ts
// VERSION: 1.0.0
// START_MODULE_CONTRACT:
// PURPOSE: [Shared Zod schemas for claim profile form validation — both client and server side]
// SCOPE: [claim form, API route validation, shared validation rules]
// INPUT: [Form field values]
// OUTPUT: [Zod validated schema + inferred types]
// KEYWORDS: [DOMAIN(claim): claim, validation, zod, schema; CONCEPT(validation): server-side, client-side]
// LINKS: [USES_API(zod): zod for schema validation]
// END_MODULE_CONTRACT

import { z } from "zod";

/**
 * Claim profile form schema.
 * Валидирует поля заявки на подтверждение профиля ЧОП.
 * Используется как на клиенте (react-hook-form), так и на сервере (API route).
 */
export const claimFormSchema = z.object({
  companyName: z
    .string()
    .min(2, "Название компании — обязательное поле, минимум 2 символа")
    .max(500, "Название компании не может превышать 500 символов"),
  inn: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^\d{10}$|^\d{12}$/.test(val),
      "ИНН должен содержать 10 цифр (юрлицо) или 12 цифр (ИП)"
    ),
  email: z
    .string()
    .min(1, "Эл. почта — обязательное поле")
    .email("Введите корректный адрес эл. почты"),
  phone: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^[\d\s\+\-\(\)]{7,20}$/.test(val),
      "Введите корректный номер телефона"
    ),
  comment: z
    .string()
    .max(2000, "Комментарий не может превышать 2000 символов")
    .optional(),
  consent: z
    .boolean()
    .refine((val) => val === true, "Необходимо согласие на обработку ПДн (152-ФЗ)"),
});

export type ClaimFormValues = z.infer<typeof claimFormSchema>;

/**
 * Server-side validation function.
 * Возвращает { success: true, data } или { success: false, error: string }.
 */
export function validateClaimForm(data: unknown) {
  const result = claimFormSchema.safeParse(data);
  if (!result.success) {
    const firstError = result.error.issues[0];
    return {
      success: false as const,
      error: firstError?.message || "Ошибка валидации формы",
    };
  }
  return { success: true as const, data: result.data };
}
